import { Loading } from '@/components/loading';
import { PersonaAvatar } from '@/components/persona-avatar';
import {
  PersonaModal,
  type UpdatePersonaCallback
} from '@/components/persona-modal';
import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { useAuth } from '@/contexts/AuthContext';
import { useAuthedApi } from '@/hooks/use-authed-api';
import { useBreakPoints } from '@/hooks/use-break-points';
import { ArrowUp } from '@/icons/ArrowUp';
import { Check } from '@/icons/Check';
import { ChevronRight } from '@/icons/ChevronRight';
import { Ellipsis } from '@/icons/Ellipsis';
import { X } from '@/icons/X';
import { generateAPIUrl } from '@/utils/api-url';
import { cn } from '@/utils/cn';
import { decryptMessages, encrypt, uint8ArrayToHex } from '@/utils/encryption';
import type { CompletionModelKey } from '@/utils/llm-models';
import { stripMemoryTags } from '@/utils/memory';
import { useChat } from '@ai-sdk/react';
import {
  BottomSheetModal,
  BottomSheetModalProvider,
  BottomSheetTextInput,
  BottomSheetView
} from '@gorhom/bottom-sheet';
import { HeaderBackButton } from '@react-navigation/elements';
import { FlashList } from '@shopify/flash-list';
import { BlurView } from 'expo-blur';
import {
  Link,
  Redirect,
  Stack,
  router,
  useLocalSearchParams
} from 'expo-router';
import Head from 'expo-router/head';
import { fetch as expoFetch } from 'expo/fetch';
import { hc } from 'hono/client';
import { PencilLine } from 'lucide-react-native';
import type * as React from 'react';
import {
  Fragment,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import {
  ActivityIndicator,
  Dimensions,
  Keyboard,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  StyleSheet,
  TextInput,
  View
} from 'react-native';
import Animated from 'react-native-reanimated';
import {
  SafeAreaView,
  useSafeAreaInsets
} from 'react-native-safe-area-context';
import { v7 as uuid } from 'uuid';
import type { ApiChatMessageIdType } from '../../../api/chat-message/[...everything]+api';
import type { ApiChatIdType } from '../../../api/chat/[id]+api';

const chatIdClient = hc<ApiChatIdType>(generateAPIUrl('/'), {
  fetch: expoFetch as unknown as typeof fetch
});

const chatMessageIdClient = hc<ApiChatMessageIdType>(generateAPIUrl('/'), {
  fetch: expoFetch as unknown as typeof fetch
});

type Chat = ReturnType<typeof useChat>;
type InitialChat = Extract<
  Awaited<ReturnType<typeof getChat>>,
  { chat: any }
>['chat'];
type ChatMessage = InitialChat['messages'][number];
type DecryptedChatMessage = Omit<ChatMessage, 'contentEnc'> & {
  content: string;
};
type DecryptedChat = Omit<InitialChat, 'messages'> & {
  messages: DecryptedChatMessage[];
};
type EditablePersona = NonNullable<DecryptedChat['persona']> & {
  model?: CompletionModelKey;
};

async function getChat(token: string, id: string) {
  const res = await chatIdClient.api.chat[':id'].$get(
    { param: { id } },
    { headers: { Authorization: `Bearer ${token}` } }
  );

  return res.json();
}

async function editChatMessage(
  token: string,
  messageId: string,
  contentEnc: string
) {
  const res = await chatMessageIdClient.api['chat-message'][':id'].edit.$put(
    { param: { id: messageId }, json: { content: contentEnc } },
    { headers: { Authorization: `Bearer ${token}` } }
  );

  return res.json();
}

const dimensions = Dimensions.get('window');

export default function ChatIndex() {
  const { id } = useLocalSearchParams() as { id: string };
  const getChatCallback = useCallback(
    (token: string) => getChat(token, id),
    [id]
  );
  const { loading, data, authState } = useAuthedApi(getChatCallback);

  if (loading || !authState.decryptionKey) {
    return <Loading />;
  }

  if ('error' in data) {
    if (data.error === 'persona_archived') {
      return (
        <Redirect
          href={`/persona-archive/${data.personaId}?fromChatId=${id}`}
        />
      );
    }
    return (
      <View className="flex-1 items-center justify-center">
        <Text>{data.error}</Text>
        <Text className="mt-4">
          <Link href="/chat">Back</Link>
        </Text>
      </View>
    );
  }

  const decryptedChat: DecryptedChat = {
    ...data.chat,
    messages: decryptMessages(data.chat.messages, authState.decryptionKey).map(
      (message) => ({
        ...message,
        content: stripMemoryTags(message.content)
      })
    )
  };

  return <ChatScreen chat={decryptedChat} />;
}

function ChatScreen({ chat }: { chat: DecryptedChat }) {
  const { authState } = useAuth();
  const [persona, setPersona] = useState<EditablePersona>({
    ...chat.persona!,
    model: chat.model ?? undefined
  });

  const flashListRef = useRef<FlashList<any>>(null);
  const [flashListLoaded, setFlashListLoaded] = useState(false);
  const [isPersonaModalVisible, setIsPersonaModalVisible] =
    useState<boolean>(false);
  const [showMessageModal, setShowMessageModal] = useState<'edit' | null>(null); // TODO: 'report' message modal
  const bottomSheetModalRef = useRef<BottomSheetModal>(null);
  const insets = useSafeAreaInsets();

  const onPersonaModalClose = () => {
    setIsPersonaModalVisible(false);
  };

  const updatePersona: UpdatePersonaCallback = useCallback((updates) => {
    setPersona((prevPersona) => ({
      ...prevPersona,
      ...updates
    }));
  }, []);

  // const scrollOffset = useRef(0);

  const {
    messages,
    setMessages,
    reload,
    error,
    setInput,
    input,
    handleSubmit,
    status,
    data,
    stop
  } = useChat({
    id: chat.id,
    initialMessages: chat.messages.map((message) => ({
      ...message,
      annotations: message.wasEdited ? [{ type: 'edit' }] : [],
      createdAt: new Date(message.createdAt)
    })),
    fetch: expoFetch as unknown as typeof globalThis.fetch,
    api: generateAPIUrl(`/api/chat/${chat.id}`),
    onFinish: (message) => {
      const newMessageCount = message.content
        .split('\n')
        .filter((content) => content.trim() !== '').length;

      // only scroll down for short messages, longer messages take longer to read
      if (newMessageCount <= 3) {
        flashListRef.current?.scrollToEnd({ animated: true });
      } else {
        // TODO? or should we scroll _while_ the message is generating?
        // const estimatedItemHeight = 36;
        // const heightDifference = 3 * estimatedItemHeight;
        // // Scroll down only 3 items
        // flashListRef.current?.scrollToOffset({
        //   offset: scrollOffset.current + heightDifference,
        //   animated: true
        // });
      }
    },
    onError: (error) => {
      alert(error.message);
      console.error(error, 'ERROR');
    },
    headers: {
      Authorization: authState?.token ? `Bearer ${authState.token}` : ''
    },
    sendExtraMessageFields: true,
    generateId: uuid,
    experimental_prepareRequestBody({ messages }) {
      // just return the last message, we have the full history on the server
      const message = messages[messages.length - 1];
      if (!authState.encryptionKey) {
        alert('User not authenticated?');
        throw new Error('User not authenticated');
      }

      const content = encrypt(
        authState.encryptionKey,
        new TextEncoder().encode(message.content)
      );

      return {
        ...message,
        content: uint8ArrayToHex(content)
      };
    }
  });

  const reloadMessages = useCallback(() => {
    if (authState.token && authState.decryptionKey) {
      getChat(authState.token, chat.id).then((data) => {
        if ('error' in data) {
          alert(data.error);
          return;
        }

        const decryptedMessages = decryptMessages(
          data.chat.messages,
          authState.decryptionKey!
        ).map((message) => {
          return {
            ...message,
            annotations: message.wasEdited ? [{ type: 'edit' }] : [],
            content: stripMemoryTags(message.content),
            createdAt: new Date(message.createdAt)
          };
        });

        setMessages(decryptedMessages);
      });
    }
  }, [authState.token, authState.decryptionKey, chat.id]);

  useEffect(() => {
    // scroll to end whenever a new message is added
    if (flashListLoaded && messages.length > 0) {
      flashListRef.current?.scrollToEnd({ animated: true });
    }
  }, [messages.length, flashListLoaded]);

  const lastMessage = messages[messages.length - 1];

  const separatedMessages = messages.flatMap((message) => {
    return message.content
      .split('\n')
      .filter((content) => content.trim() !== '')
      .map((content, idx) => ({
        id: `msg-${message.id}-${idx}`,
        role: message.role,
        annotations: message.annotations,
        content
      }));
  });

  let waitingType: 'waitingFree' | 'waitingPaid' | undefined = undefined;
  if (Array.isArray(data) && data.length > 0) {
    const waitingItem = data.find(
      (item): item is { type: 'waitingFree' | 'waitingPaid' } =>
        typeof item === 'object' &&
        item !== null &&
        'type' in item &&
        (item.type === 'waitingFree' || item.type === 'waitingPaid')
    );
    waitingType = waitingItem?.type;
  }

  function header() {
    return (
      <Header
        persona={persona}
        onAvatarPress={() => setIsPersonaModalVisible(true)}
      />
    );
  }

  return (
    <Fragment>
      <Head>
        <title>Chat - Lexikon AI</title>
      </Head>

      <Stack.Screen
        options={{
          headerShown: true,
          header
        }}
      />
      <BottomSheetModalProvider>
        <SafeAreaView style={styles.container}>
          <KeyboardAvoidingView
            enabled={!isPersonaModalVisible && showMessageModal === null}
            behavior="padding"
            style={styles.keyboardAvoidingViewStyles}
          >
            {error && <Text>{error.message}</Text>}
            <FlashList
              onLoad={() => {
                if (messages.length > 0) {
                  flashListRef.current?.scrollToEnd({ animated: true });
                }
                setFlashListLoaded(true);
              }}
              // onScroll={(event) => {
              //   scrollOffset.current = event.nativeEvent.contentOffset.y;
              // }}
              ref={flashListRef}
              data={separatedMessages}
              renderItem={({ item, index }) => {
                const nextMessage = separatedMessages[index + 1];
                const isSameNextSender = nextMessage?.role === item.role;
                const showEdit =
                  index === separatedMessages.length - 1 &&
                  item.role === 'assistant';

                return (
                  <ChatBubble
                    isSameNextSender={isSameNextSender}
                    item={item}
                    showEdit={showEdit}
                    showEditSheet={() => {
                      Keyboard.dismiss();
                      setShowMessageModal(null);
                      bottomSheetModalRef.current?.present();
                    }}
                  />
                );
              }}
              estimatedItemSize={
                72 /* about two lines, https://shopify.github.io/flash-list/docs/estimated-item-size */
              }
              keyExtractor={(item) => item.id}
              ListHeaderComponent={
                /* to offset the absolutely positioned <Header /> */
                <Animated.View className="h-[110px]" />
              }
              ListFooterComponent={
                (status === 'submitted' || status === 'streaming') &&
                (lastMessage?.role === 'user' ||
                  (lastMessage?.role === 'assistant' &&
                    !lastMessage.content)) ? (
                  <ChatBubble
                    showEdit={false}
                    item={{
                      id: 'loading',
                      role: 'assistant',
                      content: (<Ellipsis className="text-white" />) as any,
                      parts: []
                    }}
                  />
                ) : null
              }
              keyboardDismissMode="on-drag"
              keyboardShouldPersistTaps="handled"
            />
            <ChatInput
              input={input}
              onChangeText={setInput}
              onSubmit={handleSubmit}
              flashListRef={flashListRef}
              hasMessages={messages.length > 0}
              status={status}
              waitingType={waitingType}
              stop={stop}
            />
          </KeyboardAvoidingView>
          <PersonaModal
            chatId={chat.id}
            updatePersona={updatePersona}
            persona={persona}
            isVisible={isPersonaModalVisible}
            onClose={onPersonaModalClose}
            sourceScreen="chat"
          />
        </SafeAreaView>
        <BottomSheetModal
          android_keyboardInputMode={'adjustResize'}
          enableDynamicSizing={false}
          snapPoints={
            Platform.OS === 'web' ? [160, 300] : [160, dimensions.height * 0.8]
          }
          topInset={insets.top}
          bottomInset={insets.bottom}
          backgroundStyle={styles.bottomSheetBackground}
          handleIndicatorStyle={styles.bottomSheetHandleIndicator}
          ref={bottomSheetModalRef}
        >
          <BottomSheetView style={styles.bottomSheetContent}>
            {showMessageModal === 'edit' && lastMessage.role === 'assistant' ? (
              <EditMessage
                message={lastMessage}
                onClose={() => {
                  bottomSheetModalRef.current?.collapse();
                  setShowMessageModal(null);
                }}
                onEdit={() => {
                  reloadMessages();
                  bottomSheetModalRef.current?.close();
                  setShowMessageModal(null);
                }}
              />
            ) : (
              <Fragment>
                <Button
                  onPress={() => {
                    bottomSheetModalRef.current?.expand();
                    setShowMessageModal('edit');
                  }}
                  className="mb-4 w-full rounded-full web:w-1/2"
                >
                  <Text>Edit Message</Text>
                </Button>
                <Button
                  onPress={() => {
                    bottomSheetModalRef.current?.close();
                    setShowMessageModal(null);
                    reload();
                  }}
                  className="w-full rounded-full web:w-1/2"
                >
                  <Text>Generate Again</Text>
                </Button>
              </Fragment>
            )}
          </BottomSheetView>
        </BottomSheetModal>
      </BottomSheetModalProvider>
    </Fragment>
  );
}

function Header({
  persona,
  onAvatarPress
}: {
  persona: NonNullable<DecryptedChat['persona']>;
  onAvatarPress: () => void;
}) {
  const insets = useSafeAreaInsets();
  const maxNameLength = Platform.OS === 'web' ? 75 : 30;

  return (
    <BlurView
      tint="dark"
      intensity={100}
      experimentalBlurMethod="dimezisBlurView"
      style={[
        styles.headerPosition,
        {
          paddingTop: insets.top
        }
      ]}
    >
      <View className="flex-row items-center justify-between px-4 py-2">
        <View className="flex-row items-center">
          <HeaderBackButton
            tintColor="white"
            onPress={() => {
              router.replace('/chat');
            }}
          />
        </View>
        <Pressable
          onPress={onAvatarPress}
          className="items-center gap-2 active:opacity-70"
        >
          <PersonaAvatar persona={persona!} />
          <View className="flex flex-row items-center">
            <Text numberOfLines={1}>
              {persona.name && persona.name.length > maxNameLength
                ? `${persona.name.slice(0, maxNameLength)}…`
                : persona?.name || 'Unknown'}
            </Text>
            <ChevronRight color="#ffffff" size={16} className="mt-px" />
          </View>
        </Pressable>
        <View className="w-9">
          {/* <HeaderButton tintColor="white" onPress={newChat}>
            <MessageCirclePlus size={32} color="white" />
          </HeaderButton> */}
        </View>
      </View>
    </BlurView>
  );
}

function ChatBubble({
  item,
  isSameNextSender,
  showEdit,
  showEditSheet
}: {
  showEdit: boolean;
  isSameNextSender?: boolean;
  item: Chat['messages'][number] & { content: string | JSX.Element };
  showEditSheet?: () => void;
}) {
  const isMe = item.role === 'user';
  const { isCompact } = useBreakPoints();
  const wasEdited =
    !isSameNextSender &&
    item.annotations?.some(
      (annotation) =>
        annotation &&
        typeof annotation === 'object' &&
        'type' in annotation &&
        annotation.type === 'edit'
    );

  return (
    <View
      className={cn(
        Platform.OS === 'web' && !isCompact ? 'self-center w-3/4' : ''
      )}
    >
      <View
        className={cn(
          'justify-center px-2 pb-3.5',
          isSameNextSender ? 'pb-1' : 'pb-2',
          isMe ? 'items-end pl-16' : 'items-start pr-16'
        )}
      >
        <View>
          <View
            className={cn(
              'absolute bottom-0 items-center justify-center',
              isMe ? '-right-2.5' : '-left-2'
            )}
          >
            {/* little conversation bubble 'tail' only for iOS? */}
            {Platform.OS === 'ios' && (
              <>
                <View
                  className={cn(
                    'h-5 w-5 rounded-full',
                    isMe ? 'bg-primary' : 'bg-muted'
                  )}
                />
                {/* These next two views are hidden circles designed to cover up parts of the previous circle */}
                <View
                  className={cn(
                    'absolute h-5 w-5 rounded-full bg-background',
                    isMe ? '-right-2' : 'right-2'
                  )}
                />
                <View
                  className={cn(
                    'absolute h-5 w-5 -translate-y-1 rounded-full bg-background',
                    isMe ? '-right-2' : 'right-2'
                  )}
                />
              </>
            )}
          </View>
          <View
            style={styles.borderCurve}
            className={cn(
              'rounded-2xl bg-muted px-3 py-1.5 dark:bg-muted-foreground',
              Platform.OS === 'ios' && 'dark:bg-muted',
              isMe && 'bg-primary dark:bg-primary'
            )}
          >
            {typeof item.content === 'string' ? (
              <Text className={cn(isMe ? 'text-black' : 'text-white')}>
                {(item.content || '').trim()}
              </Text>
            ) : (
              item.content
            )}
          </View>
          {wasEdited ? (
            <View>
              <Text className="text-gray-400 pt-1 pl-2 text-sm">(edited)</Text>
            </View>
          ) : null}
        </View>
      </View>
      {showEdit && (
        <Pressable
          className="absolute right-4 top-2 web:right-6"
          onPress={() => showEditSheet?.()}
        >
          <PencilLine size={24} color="white" />
        </Pressable>
      )}
    </View>
  );
}

type ChatInputProps = {
  input: string;
  onChangeText: (newInput: string) => void;
  onSubmit: () => void;
  flashListRef: React.RefObject<FlashList<any>>;
  focusOnMount?: boolean;
  hasMessages?: boolean;
  waitingType?: 'waitingFree' | 'waitingPaid';
  status?: ReturnType<typeof useChat>['status'];
  stop: () => void;
};
// don't add state here: https://shopify.github.io/flash-list/docs/recycling
const ChatInput = forwardRef<TextInput, ChatInputProps>(
  (
    {
      input,
      onChangeText,
      onSubmit,
      flashListRef,
      hasMessages,
      focusOnMount = false,
      waitingType,
      status,
      stop
    },
    ref
  ) => {
    useEffect(() => {
      if (focusOnMount) {
        (ref as React.RefObject<TextInput>).current?.focus();
      }
    }, [focusOnMount]);

    useEffect(() => {
      const showSubscription = Keyboard.addListener('keyboardDidShow', () => {
        if (hasMessages) {
          flashListRef.current?.scrollToEnd({ animated: true });
        }
      });

      return () => {
        showSubscription.remove();
      };
    }, [flashListRef, hasMessages]);

    const { isCompact } = useBreakPoints();
    const isRunning = status === 'streaming' || status === 'submitted';

    return (
      <View
        className={cn(
          Platform.OS === 'web' && !isCompact ? 'self-center w-3/4' : ''
        )}
      >
        <View className="flex-row items-end gap-2 bg-background px-4 py-2">
          <ChatTextInput
            ref={ref}
            className="flex-1 rounded-[20] bg-muted py-[8]"
            placeholder="Message"
            multiline
            value={input}
            onChangeText={onChangeText}
          />
          <Button
            size="icon"
            className="android:h-12 android:w-12 rounded-full bg-primary web:inline-flex web:self-center"
            disabled={input.length === 0 || isRunning}
            onPress={() => {
              onSubmit();
              // Keyboard.dismiss();
            }}
          >
            <ArrowUp size={20} className="h-6 w-6 text-primary-foreground" />
          </Button>
        </View>
        {waitingType === 'waitingFree' ? (
          <Text className="py-2 text-gray-400 text-center text-sm">
            We're experiencing high demand on the free tier.{' '}
            <Link href="/upgrade" className="underline">
              Upgrade
            </Link>{' '}
            to avoid waiting.
          </Text>
        ) : waitingType === 'waitingPaid' ? (
          <Text className="py-2 text-gray-400 text-center text-sm">
            You're sending messages a little faster than expected, slow down a
            bit to avoid waiting.
          </Text>
        ) : (
          <Text className="py-2 text-gray-300 text-center text-sm">
            This is A.I., not a real person. All responses are fictional.
          </Text>
        )}
      </View>
    );
  }
);

ChatInput.displayName = 'ChatInput';

const ChatTextInput = forwardRef<
  React.ElementRef<typeof TextInput>,
  React.ComponentPropsWithoutRef<typeof TextInput> & {
    noFocus?: boolean;
    autoFocus?: boolean;
  }
>(
  (
    {
      className,
      placeholderClassName,
      noFocus = false,
      autoFocus = false,
      ...props
    },
    ref
  ) => {
    return (
      <TextInput
        ref={ref}
        autoFocus={autoFocus}
        className={cn(
          'native:min-h-10 native:text-md native:leading-[1.25] min-h-10 rounded-md bg-background px-4 text-base text-foreground file:border-0 file:bg-transparent file:font-medium placeholder:text-muted-foreground web:flex web:w-full web:py-2 lg:text-sm',
          'web:ring-offset-background web:focus-visible:outline-none web:focus-visible:ring-2 web:focus-visible:ring-ring web:focus-visible:ring-offset-2',
          noFocus && 'web:focus-visible:ring-0 web:focus-visible:ring-offset-0',
          props.editable === false && 'opacity-50 web:cursor-not-allowed',
          className
        )}
        placeholderClassName={cn('text-muted-foreground', placeholderClassName)}
        {...props}
      />
    );
  }
);

ChatTextInput.displayName = 'ChatTextInput';

function EditMessage({
  message,
  onClose,
  onEdit
}: {
  message: Chat['messages'][number];
  onClose: () => void;
  onEdit: () => void;
}) {
  const [content, setContent] = useState((message.content || '').trim());
  const { authState } = useAuth();
  const [isSaving, setIsSaving] = useState(false);

  const handleEdit = async () => {
    setIsSaving(true);
    try {
      if (content.length === 0) {
        onClose();
        return;
      }

      if (!authState.encryptionKey || !authState.token) {
        alert('Encryption key or token not available');
        onClose();
        return;
      }

      const encryptedContent = encrypt(
        authState.encryptionKey,
        new TextEncoder().encode(content)
      );

      const result = await editChatMessage(
        authState.token,
        message.id,
        uint8ArrayToHex(encryptedContent)
      );
      if ('error' in result) {
        alert(`Failed to edit message: ${result.error}`);
      } else {
        onEdit();
      }
    } catch (error) {
      console.error('Error editing message:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const characterLimit = 1024;

  return (
    <View className="w-full web:w-1/2">
      <BottomSheetTextInput
        autoFocus
        value={content}
        onChangeText={setContent}
        multiline
        numberOfLines={10}
        textAlignVertical="top"
        className="mb-3 self-stretch rounded-xl bg-muted p-3 text-white"
      />
      <View className="flex flex-row justify-between border-t-2 border-primary">
        <Text className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {content.length}/{characterLimit}
        </Text>
        <View className="mt-2 flex flex-row gap-4">
          <Pressable onPress={onClose}>
            <X className="text-red-400" />
          </Pressable>

          {isSaving ? (
            <View>
              <ActivityIndicator color="white" />
            </View>
          ) : (
            <Pressable onPress={handleEdit}>
              <Check className="text-green-400" />
            </Pressable>
          )}
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  // needed for header blur on android/web
  headerPosition: {
    borderBottomWidth: 1,
    borderBottomColor: '#1d312e',
    position: 'absolute',
    zIndex: 50,
    top: 0,
    left: 0,
    right: 0
  },
  keyboardAvoidingViewStyles: {
    flex: 1
  },
  borderCurve: {
    borderCurve: 'continuous'
  },
  container: {
    flex: 1
  },
  bottomSheetBackground: {
    backgroundColor: '#000'
  },
  bottomSheetContent: {
    flex: 1,
    alignItems: 'center',
    padding: 20
  },
  bottomSheetHandleIndicator: {
    backgroundColor: '#fff'
  },
  contentContainerStyles: {
    ...(Platform.OS === 'web' && dimensions.width > 768
      ? {
          alignSelf: 'center',
          width: '50%'
        }
      : {})
  }
});
